import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { LocalDateTime } from "@js-joda/core";
import { motion } from "framer-motion";
import React from "react";
import { AddressComponents } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { globalize, translate } from "../../../translate";
import { getColor } from "../../../utils/generateColor";
import { CurrencyCircleDollarIcon } from "../../../utils/icones";
import {
  getStatusColor,
  getStatusText,
  getVisitDuration,
  getVisitStatus,
  getVisitTypeText,
  VisitStatus,
} from "../../AvailableVisitsAndTasksList";
import { calcGeoDistance } from "../../DistanceLabel";
import Flex from "../../Generic/Flex";
import { metersToMilesRounded } from "../../VisitListComponents";
import { getVisitPriceAndHourTexts } from "../main-utils";

type Props = {
  caregiver: Pick<AppCaregiver, "addressGeoLocation">;
  visit: Visit;
  onClick: (visit: Visit) => void;
};

function VisitMapCard(props: Props, ref: React.Ref<HTMLDivElement>) {
  const { patientInitials, startTimeLocal, endTimeLocal, location, type } =
    props.visit.publicVisitBroadcastDetails;
  const [propertiesRef, setPropertiesRef] = React.useState<HTMLDivElement | null>(null);
  const { hoursText, priceText } = getVisitPriceAndHourTexts(props.visit);

  return (
    <Root ref={ref} onClick={() => props.onClick(props.visit)}>
      <Header>
        <Avatar>{patientInitials}</Avatar>
        <HeaderContent>
          <ScheduleDate>{formatDate(startTimeLocal, endTimeLocal)}</ScheduleDate>
          <Address>{formatAddress(location)}</Address>
        </HeaderContent>
        <VisitPrice>
          <Flex direction="column">
            <VisitPriceValue>{priceText}</VisitPriceValue>
            <VisitHoursValue>{hoursText}</VisitHoursValue>
          </Flex>
          <VisitPriceIcon as={CurrencyCircleDollarIcon} />
        </VisitPrice>
      </Header>
      <VisitSecondHeader>
        <VisitTypeAndDuration>
          <VisitType>{getVisitTypeText(type)}</VisitType> /{" "}
          <VisitDuration>{getVisitDuration(props.visit)}</VisitDuration>
        </VisitTypeAndDuration>
      </VisitSecondHeader>
      <VisitFooter>
        <VisitProperties>
          <VisitPropertiesContainer
            isScrollable={
              propertiesRef ? propertiesRef.scrollWidth === propertiesRef.clientWidth : false
            }
            ref={setPropertiesRef}
            transition={{
              duration: 3,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "mirror",
              repeatDelay: 2,
            }}
            animate={{
              x: [
                "0px",
                propertiesRef
                  ? `-${propertiesRef.scrollWidth - propertiesRef.clientWidth}px`
                  : "0px",
              ],
            }}
          >
            <VisitProperty>
              {metersToMilesRounded(
                calcGeoDistance(
                  props.caregiver.addressGeoLocation,
                  props.visit.publicVisitBroadcastDetails.location.location,
                  "K"
                ) * 1000
              )}{" "}
              {translate(STRINGS.MILES_SHORT)}
            </VisitProperty>

            <VisitProperty>
              {props.visit.publicVisitBroadcastDetails.patientFirstName}
            </VisitProperty>

            <VisitProperty>
              {props.visit.publicVisitBroadcastDetails.patientGender === "F"
                ? translate(STRINGS.FEMALE)
                : translate(STRINGS.MALE)}
            </VisitProperty>

            {props.visit.publicVisitBroadcastDetails.visitInfo?.map((property) => (
              <VisitProperty key={property}>{property}</VisitProperty>
            ))}
          </VisitPropertiesContainer>
        </VisitProperties>
        <VisitMapCardStatus visit={props.visit} />
      </VisitFooter>
    </Root>
  );
}

export default React.forwardRef(VisitMapCard);

function formatDate(startTimeLocal: LocalDateTime, endTimeLocal: LocalDateTime) {
  const format = (date: LocalDateTime) =>
    globalize().formatLocalDateTime(
      date,
      startTimeLocal.year() === endTimeLocal.year() ? { skeleton: "MMMd" } : { date: "short" }
    );

  const startTime = format(startTimeLocal);
  const endTime = format(endTimeLocal);

  return startTime === endTime ? startTime : `${startTime} - ${endTime}`;
}

function formatAddress(address: AddressComponents): string {
  return [address.sublocalityLevel1, address.neighborhood, address.locality]
    .filter(Boolean)
    .join(", ");
}

const Root = styled(motion.div)`
  background: white;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div<{ children: string }>`
  --size: 40px;
  background-color: ${({ children }) => getColor(children)};
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  color: white;
  width: var(--size);
  height: var(--size);
  border-radius: 16px;
  font-size: 14px;
  font-weight: 700;
  border: 4px solid white;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ScheduleDate = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
`;

const Address = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: var(--tw-slate-500);
`;

const VisitPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-start: auto;
  align-self: flex-start;
  padding: 4px;
`;

const VisitPriceValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
`;

const VisitHoursValue = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: var(--tw-slate-400);
  text-align: right;
`;

const VisitPriceIcon = styled.svg`
  font-size: 24px;
  color: var(--tw-slate-400);
`;

const VisitSecondHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px;
`;

const VisitTypeAndDuration = styled.div`
  font-size: 14px;
  line-height: 20px;
`;

const VisitType = styled.span`
  color: var(--tw-slate-500);
  font-weight: 600;
`;

const VisitDuration = styled.strong`
  color: var(--tw-slate-900);
  font-weight: 700;
  text-transform: lowercase;
`;

const VisitProperty = styled.span`
  font-weight: 700;
  font-size: 11px;
  line-height: 20px;
  text-transform: uppercase;
  text-align: center;
  white-space: nowrap;
  color: #475569;
`;

const VisitProperties = styled.div`
  background-color: var(--tw-slate-50);
  border-radius: 8px;
  padding: 6px 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

const VisitPropertiesContainer = styled(motion.div)<{ isScrollable: boolean }>`
  display: flex;
  width: 100%;

  ${(props) => {
    if (props.isScrollable) {
      return css`
        align-items: center;
        justify-content: center;
      `;
    }

    return css``;
  }}

  ${VisitProperty} + ${VisitProperty} {
    &::before {
      content: "";
      display: inline-block;
      width: 1px;
      height: 5px;
      border-left: 1px solid var(--tw-slate-300);
      margin-inline-start: 12px;
      padding-inline-start: 12px;
    }
  }
`;

function VisitMapCardStatus(props: { visit: Visit }) {
  const status = getVisitStatus(props.visit);
  const statusAsText = getStatusText(status).split(" ")[0];

  return <Root2 status={status}>{statusAsText}</Root2>;
}

const Root2 = styled.div<{ status: VisitStatus }>`
  --bg-color: black;
  --text-color: white;
  background-color: var(--bg-color);
  color: var(--text-color);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 8px 7px;
  text-transform: capitalize;
  flex-shrink: 0;

  display: flex;

  ${(props) => {
    switch (getStatusColor(props.status)) {
      case "blue":
        return css`
          --bg-color: var(--tw-blue-600);
          --text-color: var(--tw-blue-50);
        `;
      case "yellow":
        return css`
          --bg-color: var(--tw-amber-50);
          --text-color: var(--tw-amber-600);
        `;
      case "green":
        return css`
          --bg-color: var(--tw-emerald-50);
          --text-color: var(--tw-emerald-600);
        `;
      case "slate":
        return css`
          --bg-color: var(--tw-slate-100);
          --text-color: var(--tw-slate-600);
        `;
    }
  }}
`;

const VisitFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;
